import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Panel, TactLoader } from "../components/Tactical";
import ReliabilityBadge from "../components/ReliabilityBadge";
import SocialCard from "../components/SocialCard";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import {
  ArrowLeft,
  Camera,
  Crown,
  Gamepad2,
  MessageCircle,
  Shield,
  Smartphone,
  Swords,
  Trophy,
  UserPlus,
} from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], { month: "short", day: "2-digit", year: "numeric" });
  } catch { return iso; }
}
function fmtShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString([], { month: "short", day: "2-digit" });
  } catch { return iso; }
}

function RoleBadge({ role }) {
  if (role === "admin") return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#E11D48] text-[#E11D48] px-2 py-0.5">
      <Shield className="w-3 h-3" /> Admin
    </span>
  );
  if (role === "moderator") return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#7C3AED] text-[#7C3AED] px-2 py-0.5">
      <Shield className="w-3 h-3" /> Moderator
    </span>
  );
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] px-2 py-0.5">
      Player
    </span>
  );
}

function PlatformBadge({ platform }) {
  const isConsole = platform === "console";
  const Icon = isConsole ? Gamepad2 : Smartphone;
  const label = isConsole ? "PS5 · STEAM" : "MOBILE";
  const color = isConsole ? "#7C3AED" : "#EA580C";
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border"
      style={{ borderColor: color, color }}
      data-testid="profile-platform-badge"
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function StatBox({ label, value, tone = "default", testId }) {
  const tones = {
    default: "text-[#09090B] dark:text-[#F4F4F5]",
    accent: "text-[#7C3AED]",
    win: "text-[#34C759]",
    loss: "text-[#E11D48]",
  };
  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{label}</div>
      <div className={`font-heading text-3xl ${tones[tone]} leading-tight mt-1`}>{value}</div>
    </div>
  );
}

function OutcomePill({ outcome }) {
  if (outcome === "W") return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#34C759] border border-[#34C759] px-1.5 py-0.5">W</span>;
  if (outcome === "L") return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] border border-[#E11D48] px-1.5 py-0.5">L</span>;
  return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] border border-[#71717A] px-1.5 py-0.5">D</span>;
}

export default function PlayerProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [socialTab, setSocialTab] = useState(null); // "followers" | "following" | null
  const [socialRows, setSocialRows] = useState([]);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/players/${encodeURIComponent(username)}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => { alive = false; };
  }, [username]);

  useEffect(() => {
    if (!socialTab || !data?.user?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data: lst } = await api.get(`/users/${data.user.id}/${socialTab}`);
        if (alive) setSocialRows(lst.rows || []);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [socialTab, data?.user?.id]);

  const doToggleFollow = async () => {
    if (!data?.user) return;
    const p = data.user;
    setFollowBusy(true);
    try {
      if (p.is_following) {
        await api.delete(`/users/${p.id}/follow`);
        setData((prev) => prev && { ...prev, user: {
          ...prev.user,
          is_following: false,
          is_friend: false,
          followers_count: Math.max(0, (prev.user.followers_count || 1) - 1),
        }});
      } else {
        await api.post(`/users/${p.id}/follow`);
        setData((prev) => prev && { ...prev, user: {
          ...prev.user,
          is_following: true,
          is_friend: prev.user.is_followed_by ? true : prev.user.is_friend,
          followers_count: (prev.user.followers_count || 0) + 1,
        }});
      }
    } catch { /* noop */ }
    finally { setFollowBusy(false); }
  };

  useEffect(() => {
    const prev = document.title;
    if (data?.user?.username) document.title = `${data.user.username} · ePlay Profile`;
    return () => { document.title = prev; };
  }, [data?.user?.username]);

  // Must be declared BEFORE any early return to satisfy react-hooks/rules-of-hooks.
  const avatarInputRef = React.useRef(null);

  if (err === "notfound") return (
    <div className="max-w-md mx-auto py-16 text-center" data-testid="profile-not-found">
      <div className="font-heading text-2xl uppercase tracking-wider mb-2">Player Not Found</div>
      <div className="font-mono text-xs text-[#71717A] mb-6">No ePlay profile for "{username}".</div>
      <button onClick={() => nav(-1)} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-5 py-2 font-mono text-[10px] uppercase tracking-[0.3em] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors">Go Back</button>
    </div>
  );
  if (!data) return <TactLoader label="LOADING PROFILE" />;

  const { user: p, summary: s, h2h, leagues, recent } = data;
  const isMe = user && user.id === p.id;
  const form = recent.slice(0, 5).map((m) => m.outcome).reverse();

  // Build a card-shape object from the profile payload so the FUT preview card
  // matches exactly what others see on /social.
  const cardPayload = {
    id: p.id,
    username: p.username,
    game_id: p.game_id,
    platform: p.platform,
    avatar_url: p.avatar_url,
    reliability: p.reliability?.score,
    reliability_flagged: p.reliability?.flagged,
    followers_count: p.followers_count,
    following_count: p.following_count,
    is_following: p.is_following,
    is_followed_by: p.is_followed_by,
    is_friend: p.is_friend,
    played: s?.played,
    won: s?.won,
    drawn: s?.drawn,
    lost: s?.lost,
    points: s?.points,
    win_rate: s?.win_rate,
    current_league: p.current_league || (leagues && leagues[0]
      ? { id: leagues[0].id, name: leagues[0].name, platform: leagues[0].platform, status: leagues[0].status }
      : null),
  };

  const onAvatarPick = async (file) => {
    if (!file || !isMe) return;
    if (file.size > 1024 * 1024) {
      toast.error("Image too large (max 1MB). Crop or compress first.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const { data: up } = await api.post("/users/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated");
      setData((prev) => prev && { ...prev, user: { ...prev.user, avatar_url: up.avatar_url } });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="player-profile">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] transition-colors" data-testid="profile-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      {/* FUT-style card preview — same widget everyone else sees on /social */}
      <div className="flex flex-col items-center gap-3" data-testid="profile-card-preview">
        <div className="relative w-full max-w-xs">
          <SocialCard p={cardPayload} viewer={user} />
          {isMe && (
            <>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => onAvatarPick(e.target.files?.[0])}
                data-testid="profile-card-avatar-input"
              />
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                data-testid="profile-card-edit-photo"
                title={p.avatar_url ? "Replace profile picture" : "Upload profile picture"}
                className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-[#7C3AED] text-white flex items-center justify-center shadow-[0_4px_16px_rgba(124,58,237,0.55)] ring-2 ring-white/30 hover:bg-[#6D28D9] transition-colors"
              >
                <Camera className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        {isMe && (
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] text-center max-w-xs">
            // this is exactly how other players see your card on social.
            {!p.avatar_url && <><br />Tap the <span className="text-[#7C3AED]">camera</span> to upload a photo.</>}
          </div>
        )}
      </div>

      {/* Header card */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-6" data-testid="profile-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            {/* Avatar — uploaded image or initials fallback */}
            {p.avatar_url ? (
              <img
                src={p.avatar_url}
                alt={p.username}
                data-testid="profile-avatar"
                className="w-16 h-16 object-cover rounded-full ring-2 ring-[#7C3AED] shadow-[0_0_24px_rgba(124,58,237,0.35)] flex-shrink-0"
              />
            ) : (
              <div
                className="w-16 h-16 flex items-center justify-center font-heading text-3xl bg-gradient-to-br from-[#7C3AED] to-[#EA580C] text-white shadow-[0_0_28px_rgba(124,58,237,0.35)] flex-shrink-0 rounded-full"
                aria-hidden
                data-testid="profile-avatar-initials"
              >
                {(p.username || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5]" data-testid="profile-username">{p.username}</h1>
                {p.reliability && (
                  <ReliabilityBadge
                    score={p.reliability.score}
                    flagged={p.reliability.flagged}
                    size="md"
                    testId="profile-reliability-badge"
                  />
                )}
                {p.banned && <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E11D48] border border-[#E11D48] px-2 py-0.5">BANNED</span>}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717A] mt-1">
                Game ID: <span className="text-[#09090B] dark:text-[#F4F4F5]">{p.game_id || "—"}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-3">
                <PlatformBadge platform={p.platform} />
                <RoleBadge role={p.role} />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">joined {fmt(p.created_at)}</span>
              </div>
              {/* Followers / Following */}
              <div className="flex items-center gap-4 mt-3">
                <button
                  type="button"
                  onClick={() => setSocialTab("followers")}
                  data-testid="profile-followers-count"
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED] transition-colors"
                >
                  <span className="font-heading text-lg">{p.followers_count ?? 0}</span> followers
                </button>
                <button
                  type="button"
                  onClick={() => setSocialTab("following")}
                  data-testid="profile-following-count"
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED] transition-colors"
                >
                  <span className="font-heading text-lg">{p.following_count ?? 0}</span> following
                </button>
              </div>
            </div>
          </div>
          {/* Actions */}
          {!isMe && user && user !== false && (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={doToggleFollow}
                disabled={followBusy}
                data-testid="profile-follow-btn"
                className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 transition-colors disabled:opacity-40 ${
                  p.is_friend
                    ? "bg-[#FFD700] text-black hover:bg-[#E6C200]"
                    : p.is_following
                      ? "border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10"
                      : "bg-[#00FF87] text-black hover:bg-[#5EF0B1]"
                }`}
              >
                <UserPlus className="w-3 h-3" />
                {p.is_friend ? "FRIENDS" : p.is_following ? "FOLLOWING" : "+ FOLLOW"}
              </button>
              <Link
                to={`/h2h/${p.id}`}
                data-testid="profile-play-h2h"
                className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#6D28D9] transition-colors"
              >
                <Swords className="w-3 h-3" /> H2H Room
              </Link>
              <Link
                to={`/chat/${p.id}`}
                data-testid="profile-message"
                className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#7C3AED] hover:text-white transition-colors"
              >
                <MessageCircle className="w-3 h-3" /> Message
              </Link>
            </div>
          )}
        </div>

        {/* Win rate highlight */}
        <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="profile-summary-grid">
          <StatBox testId="stat-played" label="PLAYED" value={s.played} />
          <StatBox testId="stat-won" label="WON" value={s.won} tone="win" />
          <StatBox testId="stat-drawn" label="DRAWN" value={s.drawn} />
          <StatBox testId="stat-lost" label="LOST" value={s.lost} tone="loss" />
          <StatBox testId="stat-win-rate" label="WIN %" value={`${s.win_rate}%`} tone="accent" />
          <StatBox testId="stat-points" label="POINTS" value={s.points} tone="accent" />
        </div>

        {/* Secondary row: GF / GA / GD / Form / Leagues / Pending */}
        <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-3">
          <StatBox testId="stat-gf" label="GF" value={s.gf} />
          <StatBox testId="stat-ga" label="GA" value={s.ga} />
          <StatBox testId="stat-gd" label="GD" value={`${s.gd >= 0 ? "+" : ""}${s.gd}`} tone={s.gd >= 0 ? "win" : "loss"} />
          <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-4" data-testid="stat-form">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">FORM</div>
            <div className="flex gap-1 mt-2">
              {form.length === 0 ? <span className="font-mono text-[10px] text-[#71717A]">—</span> :
                form.map((o, i) => <OutcomePill key={i} outcome={o} />)}
            </div>
          </div>
          <StatBox testId="stat-leagues" label="SEASONS" value={s.league_count} />
          {isMe ? <StatBox testId="stat-pending" label="PENDING" value={s.pending_fixtures} tone={s.pending_fixtures > 0 ? "accent" : "default"} /> :
            <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-4 opacity-50" aria-hidden>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">&nbsp;</div>
              <div className="font-heading text-3xl text-[#71717A]">·</div>
            </div>}
        </div>
      </div>

      {/* Social drawer: followers / following lists */}
      {socialTab && (
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6" data-testid={`profile-social-drawer-${socialTab}`}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">
                // {socialTab}
              </span>
              <span className="font-mono text-[10px] text-[#71717A]">{socialRows.length}</span>
            </div>
            <button
              type="button"
              onClick={() => { setSocialTab(null); setSocialRows([]); }}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            >
              close
            </button>
          </div>
          {socialRows.length === 0 ? (
            <div className="font-mono text-sm text-[#71717A]">[ EMPTY ]</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {socialRows.map((r) => (
                <Link
                  key={r.id}
                  to={`/players/${encodeURIComponent(r.username)}`}
                  className="flex items-center gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 hover:border-[#7C3AED] transition-colors"
                  data-testid={`profile-social-row-${r.id}`}
                >
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt={r.username} className="w-9 h-9 rounded-full object-cover ring-1 ring-[#7C3AED]/40" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#00FF87] flex items-center justify-center font-heading text-sm text-white">
                      {(r.username || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading uppercase text-sm text-[#09090B] dark:text-[#F4F4F5] truncate">{r.username}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">
                      {(r.platform || "mobile").toUpperCase()} · REL {r.reliability ?? 70}
                    </div>
                  </div>
                  {r.is_friend && <Trophy className="w-3 h-3 text-[#FFD700]" />}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* H2H history */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6" data-testid="profile-h2h">
        <div className="flex items-center gap-2 mb-4">
          <Swords className="w-4 h-4 text-[#7C3AED]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">// head to head history</span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{h2h.length} opponents</span>
        </div>
        {h2h.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO HEAD-TO-HEAD RECORDED YET. ]</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                  <th className="text-left py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">OPPONENT</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">P</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#34C759]">W</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">D</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48]">L</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">GF-GA</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED]">WIN %</th>
                  <th className="text-right py-2 px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">LAST</th>
                </tr>
              </thead>
              <tbody>
                {h2h.map((row) => (
                  <tr key={row.opponent_id} className="border-b border-[#E4E4E7] dark:border-[#2A2A30] hover:bg-[#FAFAFC] dark:hover:bg-[#09090B] transition-colors" data-testid={`h2h-row-${row.opponent_id}`}>
                    <td className="py-2.5 px-2">
                      <Link to={`/players/${encodeURIComponent(row.opponent_username)}`} className="font-heading uppercase text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED]">
                        {row.opponent_username}
                      </Link>
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono">{row.played}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#34C759]">{row.won}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#71717A]">{row.drawn}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#E11D48]">{row.lost}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#71717A]">{row.gf}-{row.ga}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#7C3AED]">{row.win_rate}%</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[10px] text-[#71717A]">{fmtShort(row.last_played)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent matches */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6" data-testid="profile-recent">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="w-4 h-4 text-[#EA580C]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">// last 10 matches</span>
        </div>
        {recent.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NO CONFIRMED MATCHES YET. ]</div>
        ) : (
          <ul className="space-y-1" data-testid="profile-recent-list">
            {recent.map((m) => (
              <li key={m.match_id} className="flex items-center justify-between gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2" data-testid={`recent-${m.match_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <OutcomePill outcome={m.outcome} />
                  <Link to={`/players/${encodeURIComponent(m.opponent_username)}`} className="font-heading uppercase truncate hover:text-[#7C3AED] text-[#09090B] dark:text-[#F4F4F5]">
                    vs {m.opponent_username}
                  </Link>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono text-sm text-[#09090B] dark:text-[#F4F4F5]">{m.my_score} – {m.opp_score}</span>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-[#71717A]">{fmtShort(m.confirmed_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Leagues */}
      <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] p-6" data-testid="profile-leagues">
        <div className="flex items-center gap-2 mb-4">
          <Crown className="w-4 h-4 text-[#EA580C]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">// seasons played</span>
        </div>
        {leagues.length === 0 ? (
          <div className="font-mono text-[#71717A] text-sm">[ NOT CURRENTLY IN A LEAGUE. ]</div>
        ) : (
          <div className="space-y-2">
            {leagues.map((lg) => (
              <Link
                key={lg.id}
                to={`/leagues/${lg.id}`}
                className="flex items-center justify-between gap-3 border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 hover:border-[#7C3AED] transition-colors"
                data-testid={`league-row-${lg.id}`}
              >
                <div className="font-heading uppercase text-[#09090B] dark:text-[#F4F4F5]">{lg.name}</div>
                <div className="flex gap-2 items-center">
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border"
                    style={lg.platform === "console" ? { borderColor: "#7C3AED", color: "#7C3AED" } : { borderColor: "#EA580C", color: "#EA580C" }}
                  >
                    {lg.platform === "console" ? "PS5 · STEAM" : "MOBILE"}
                  </span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${lg.status === "open" ? "text-[#34C759]" : "text-[#71717A]"}`}>
                    {lg.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
